import { motion } from "framer-motion";
import { BadgeCheck, FileWarning, ArrowUpRight, Ban } from "lucide-react";

const ROWS = [
  { label: "Paid", value: "₹299" },
  { label: "Promised", value: "108 editable docs" },
  { label: "Received", value: "Free files, repackaged", warn: true },
  { label: "Upsell pushed", value: "Yes → ₹3,000", warn: true },
  { label: "Refund offered", value: "No", warn: true },
];

export const ReceiptCard = ({ dark = false, float = true, testId = "receipt-card" }) => {
  const card = (
    <div
      data-testid={testId}
      className={
        dark
          ? "rounded-[1.15rem] bg-[#101418] p-6 sm:p-7 text-white"
          : "rounded-2xl bg-white p-6 sm:p-7 shadow-[0_24px_60px_-16px_rgba(26,86,219,0.18)] border border-black/5"
      }
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className={`font-display font-semibold text-base ${dark ? "text-white" : "text-ink"}`}>
            "Site Bundle — 108 Docs"
          </p>
          <p className={`text-xs mt-1 ${dark ? "text-gray-400" : "text-gray-500"}`}>Verified buyer report</p>
        </div>
        <span className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-verified-soft text-verified text-[11px] font-bold px-3 py-1.5 uppercase tracking-wider">
          <BadgeCheck size={13} /> Verified
        </span>
      </div>
      <div className={`my-5 ${dark ? "receipt-dash-dark" : "receipt-dash"}`} />
      <dl className="space-y-3">
        {ROWS.map((r) => (
          <div key={r.label} className="flex items-center justify-between gap-4 text-sm">
            <dt className={dark ? "text-gray-400" : "text-gray-500"}>{r.label}</dt>
            <dd className={`font-semibold text-right ${r.warn ? "text-warn" : dark ? "text-white" : "text-ink"}`}>
              {r.value}
            </dd>
          </div>
        ))}
      </dl>
      <div className={`my-5 ${dark ? "receipt-dash-dark" : "receipt-dash"}`} />
      <div className="flex items-center justify-between gap-3">
        <motion.span
          className="stamp rounded-md px-3 py-1.5 text-verified font-display font-bold text-xs tracking-[0.15em] inline-block"
          initial={{ scale: 2.6, opacity: 0, rotate: -16 }}
          whileInView={{ scale: 1, opacity: 1, rotate: -4 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ type: "spring", stiffness: 320, damping: 14, delay: 0.9 }}
        >
          PROOF ON FILE
        </motion.span>
        <span className={`text-[10px] uppercase tracking-[0.18em] font-semibold ${dark ? "text-gray-500" : "text-gray-400"}`}>
          Payment + product screenshots
        </span>
      </div>
    </div>
  );

  const inner = float ? (
    <motion.div
      animate={{ y: [0, -14, 0], rotate: [-1.5, 1, -1.5] }}
      transition={{ repeat: Infinity, duration: 7, ease: "easeInOut" }}
    >
      {card}
    </motion.div>
  ) : (
    card
  );

  return dark ? <div className="tracing-border">{inner}</div> : inner;
};

export const ReceiptFloatExtras = () => (
  <>
    <motion.div
      data-testid="hero-chip-refund"
      className="absolute -left-6 top-8 hidden sm:flex items-center gap-2 rounded-xl bg-white border border-black/5 shadow-lg px-4 py-2.5"
      animate={{ y: [0, -10, 0] }}
      transition={{ repeat: Infinity, duration: 5, ease: "easeInOut", delay: 0.6 }}
    >
      <Ban size={15} className="text-warn" />
      <span className="text-xs font-semibold">Refund asked — no reply</span>
    </motion.div>
    <motion.div
      data-testid="hero-chip-upsell"
      className="absolute -right-4 bottom-10 hidden sm:flex items-center gap-2 rounded-xl bg-white border border-black/5 shadow-lg px-4 py-2.5"
      animate={{ y: [0, 10, 0] }}
      transition={{ repeat: Infinity, duration: 6, ease: "easeInOut", delay: 1.2 }}
    >
      <ArrowUpRight size={15} className="text-gold" />
      <span className="text-xs font-semibold">Upsell: ₹299 → ₹40,000</span>
    </motion.div>
    <motion.div
      data-testid="hero-chip-file"
      className="absolute -left-2 -bottom-6 hidden sm:flex items-center gap-2 rounded-xl bg-white border border-black/5 shadow-lg px-4 py-2.5"
      animate={{ y: [0, -8, 0] }}
      transition={{ repeat: Infinity, duration: 5.5, ease: "easeInOut", delay: 2 }}
    >
      <FileWarning size={15} className="text-warn" />
      <span className="text-xs font-semibold">"Software" was an .xlsx file</span>
    </motion.div>
  </>
);
